import { useState } from 'react';
import { css } from '@emotion/react';
import { Icon, IconName } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Button, NakedButton, useTooltip } from '@frontend/design-system';
import { controlStyles } from './styles';

type LightboxControlProps = {
  name: IconName;
  onClick: () => void;
  tooltipMessage?: string;
};

export const LightboxControl = ({ name, onClick, tooltipMessage }: LightboxControlProps) => {
  const { tooltipProps, triggerProps, Tooltip } = useTooltip();
  const [hovered, setHovered] = useState(false);
  return (
    <>
      <NakedButton
        {...triggerProps}
        css={[
          controlStyles,
          css`
            width: fit-content;
            &:hover {
              background: ${theme.colors.white};
              transition: all 0.2s ease;
            }
          `,
        ]}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={onClick}
      >
        <Icon name={name} color={hovered ? 'default' : 'white'} css={{ background: 'transparent', padding: 0 }} />
      </NakedButton>
      {tooltipMessage && <Tooltip {...tooltipProps}>{tooltipMessage}</Tooltip>}
    </>
  );
};

const iconButtonStyles = css`
  background: transparent;
  padding: 0;
  height: fit-content;
  &:hover {
    background: transparent;
  }
`;

export type LightboxZoomState = {
  value: number;
  onChange: (next: number) => void;
  min?: number;
  max?: number;
  step?: number;
};

type LightboxZoomSliderProps = {
  zoom: LightboxZoomState;
};

export const LightboxZoomSlider = ({ zoom }: LightboxZoomSliderProps) => {
  const { value, onChange, min = 1, max = 3, step = 0.25 } = zoom;

  const clamp = (v: number) => Math.min(max, Math.max(min, v));
  const sliderMin = 0;
  const sliderMax = 100;
  const toSlider = (v: number) => ((v - min) / (max - min)) * (sliderMax - sliderMin) + sliderMin;
  const fromSlider = (s: number) => ((s - sliderMin) / (sliderMax - sliderMin)) * (max - min) + min;

  return (
    <div css={[controlStyles, { alignItems: 'center', gap: theme.spacing(0.5) }]}>
      <Button
        iconName='minus-small'
        color='white'
        css={iconButtonStyles}
        onClick={() => onChange(clamp(value - step))}
      />
      <input
        type='range'
        min={sliderMin}
        max={sliderMax}
        value={toSlider(value)}
        onChange={(e) => onChange(clamp(fromSlider(e.target.valueAsNumber)))}
        css={{ cursor: 'pointer' }}
        aria-label='Zoom'
      />
      <Button
        iconName='plus-small'
        color='white'
        css={iconButtonStyles}
        onClick={() => onChange(clamp(value + step))}
      />
    </div>
  );
};

export type LightboxControlsProps = {
  onRotate?: () => void;
  zoom?: LightboxZoomState;
  onDownload?: () => void;
  onOpenInNewWindow?: () => void;
};

export const LightboxControls = ({ onRotate, zoom, onDownload, onOpenInNewWindow }: LightboxControlsProps) => {
  return (
    <div css={{ display: 'flex', gap: theme.spacing(1), alignItems: 'center' }}>
      {onRotate && <LightboxControl name='update-small' onClick={onRotate} tooltipMessage='Rotate' />}
      {zoom && <LightboxZoomSlider zoom={zoom} />}
      {(onDownload || onOpenInNewWindow) && (
        <div css={{ display: 'flex', gap: theme.spacing(0.5) }}>
          {onDownload && <LightboxControl name='download-small' onClick={onDownload} tooltipMessage='Download' />}
          {onOpenInNewWindow && (
            <LightboxControl
              name='external-link-small'
              onClick={onOpenInNewWindow}
              tooltipMessage='Open in new window'
            />
          )}
        </div>
      )}
    </div>
  );
};
